/**
 * TPRA Assessments API Request Hooks
 * Fetches third-party vendor assessments data
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraAssessmentsQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery } from "@tanstack/react-query";
import type { ThirdPartiesResponse, VendorsQueryParams } from "../types";

/**
 * Fetches third-party assessments data from the API
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Third parties response with assessments data
 */
export const getThirdParties = async (
  params: VendorsQueryParams
): Promise<ThirdPartiesResponse> => {
  return get<ThirdPartiesResponse>({
    url: APIEndpoints.TPRAAssessments,
    config: { params },
  });
};

/**
 * React Query hook for fetching TPRA third-party assessments
 *
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns React Query result with third parties data
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useGetThirdPartiesQuery({
 *   page: 1,
 *   page_size: 25,
 *   search_key: 'vendor name',
 *   filter_keys: ['high'],
 *   sort_by: 'vendor_name',
 *   sort_order: 'asc',
 * });
 * ```
 */
export const useGetThirdPartiesQuery = (params: VendorsQueryParams = {}) => {
  return useQuery<ThirdPartiesResponse>({
    queryKey: tpraAssessmentsQueryKeys.list(params),
    queryFn: () => getThirdParties(params),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};
